/**
 * How the clinic's clients learn that somebody is calling, and what each of
 * them does about it.
 *
 * HOW THE RING TRAVELS. One Medplum WebSocket subscription, on the
 * colleague-call CATEGORY rather than on the recipient:
 *
 *     Communication?category=<colleague-call system>|colleague-call
 *
 * That is one subscription per client, and every clinician's browser receives
 * every ring in the clinic. Two things make it the right trade rather than a
 * lazy one:
 *
 *   - IT COSTS ONE SOCKET INSTEAD OF TWO. A recipient-scoped criteria would
 *     tell me about rings aimed at me and nothing else, so learning that a call
 *     I PLACED was declined would need a second, sender-scoped subscription.
 *     Medplum caps a user at `maxUserWebSocketSubscriptions` (default 20) and
 *     the data store already spends two per resource type; a category-wide
 *     criteria answers both questions on one.
 *   - THERE IS NOTHING TO LEAK. A colleague-call row carries two Practitioner
 *     references, an instant and a status. It has no `subject` — by design,
 *     see the adapter — so no patient is named, and the clinician access policy
 *     already grants a read of every non-notification `Communication`. A
 *     recipient filter here would be a display rule wearing a privacy costume.
 *
 * The cost is real and worth stating: ring volume fans out to every open
 * client, so this is a design for one clinic and would not survive a
 * multi-tenant directory.
 *
 * WHAT IS NOT DELIVERED. A subscription only reports what happens while you
 * are listening. A ring placed before this client connected — mid-reload, mid
 * login — is never seen, and there is no catch-up search on mount. That is the
 * ring's own design rather than an omission: `RING_MAX_AGE_MS` is sixty
 * seconds and there is no sweeper, so a missed call simply disappears. A
 * backfill search would mostly resurrect rings the server has already stopped
 * answering.
 *
 * ADDRESSING IS THEREFORE THIS FILE'S JOB, and the whole reason it is pure. The
 * server enforces who may ANSWER; nothing but the code below decides who is
 * SHOWN a ring, and showing the wrong clinician one produces an answer button
 * the server then refuses.
 */

import type { Bundle, Communication } from "@medplum/fhirtypes";
import { colleagueCallFromCommunication } from "@repo/api";
import { type Call, isRingStale } from "@repo/conferencing";

/**
 * The call a notification is about, or `undefined` for anything that is not one
 * of ours.
 *
 * Medplum puts a `SubscriptionStatus` in the first entry and the resource in
 * the second. The category check inside `colleagueCallFromCommunication` is
 * load-bearing even though the criteria already filters: three aggregates share
 * `Communication`, and the parse is the only thing standing between an outbox
 * row and a state machine built for something else.
 */
export function ringFromNotification(bundle: Bundle): Call | undefined {
  const resource = bundle.entry?.[1]?.resource;
  if (resource?.resourceType !== "Communication") return undefined;
  return colleagueCallFromCommunication(resource as Communication);
}

/**
 * What this client should do with a call it has just heard about.
 *
 * `withdraw` and `unanswered` are the two halves of one fact told to opposite
 * people: a ring that stopped ringing. The colleague being rung has a ring on
 * screen that must come down; the colleague who rang is sitting in an empty
 * room waiting for someone who is not coming.
 */
export type RingDelivery =
  | { kind: "ring"; call: Call }
  | { kind: "withdraw"; callId: string }
  | { kind: "unanswered"; callId: string; status: "declined" | "abandoned" }
  /** They picked up. Told only to the caller, who has been waiting minimised. */
  | { kind: "answered"; callId: string }
  | { kind: "ignore" };

export function deliveryForRing(call: Call, meRef: string, now: Date): RingDelivery {
  if (call.to === meRef) {
    if (call.status !== "ringing") return { kind: "withdraw", callId: call.id };
    // The package's rule, not a second copy of the number. A client that
    // showed a ring past its age would offer an answer the server refuses.
    return isRingStale(call, now) ? { kind: "ignore" } : { kind: "ring", call };
  }

  if (call.from === meRef && call.status === "answered") {
    // The other signal the caller gets, and the counterpart of `unanswered`:
    // somebody is in the room now, so the card they have been waiting in has
    // something worth opening.
    return { kind: "answered", callId: call.id };
  }

  if (call.from === meRef && (call.status === "declined" || call.status === "abandoned")) {
    // `abandoned` reaches me when I hung up somewhere else — a second tab, a
    // second device. Treating it the same as a decline is right: both mean the
    // room I am waiting in has nobody else coming to it.
    return { kind: "unanswered", callId: call.id, status: call.status };
  }

  return { kind: "ignore" };
}
